import { create } from 'zustand';
import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  Timestamp,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';

interface Session {
  id: string;
  userId: string;
  description: string;
  duration: number;
  completedAt: Timestamp;
}

interface SessionState {
  sessions: Session[];
  isLoading: boolean;
  hasMore: boolean;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  addSession: (userId: string, description: string, duration: number) => Promise<void>;
  loadSessions: (userId: string, reset?: boolean) => Promise<void>;
  loadMoreSessions: (userId: string) => Promise<void>;
}

const SESSIONS_PER_PAGE = 10;

export const useSessionStore = create<SessionState>((set, get) => ({
  sessions: [],
  isLoading: false,
  hasMore: true,
  lastVisible: null,

  addSession: async (userId: string, description: string, duration: number) => {
    try {
      const docRef = await addDoc(collection(db, 'sessions'), {
        userId,
        description,
        duration,
        completedAt: Timestamp.now()
      });

      set((state) => ({
        sessions: [{
          id: docRef.id,
          userId,
          description,
          duration,
          completedAt: Timestamp.now()
        }, ...state.sessions]
      }));
    } catch (error) {
      console.error('Error adding session:', error);
    }
  },

  loadSessions: async (userId: string, reset: boolean = false) => {
    set({ isLoading: true });
    if (reset) {
      set({ sessions: [], lastVisible: null, hasMore: true });
    }
    
    try {
      const q = query(
        collection(db, 'sessions'),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
        limit(SESSIONS_PER_PAGE)
      );
      
      const querySnapshot = await getDocs(q);
      const sessions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Session[];

      set({ 
        sessions, 
        isLoading: false,
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
        hasMore: querySnapshot.docs.length === SESSIONS_PER_PAGE
      });
    } catch (error) {
      console.error('Error loading sessions:', error);
      set({ isLoading: false });
    }
  },

  loadMoreSessions: async (userId: string) => {
    const { isLoading, lastVisible, hasMore } = get();
    
    if (isLoading || !hasMore || !lastVisible) return;

    set({ isLoading: true });
    
    try {
      const q = query(
        collection(db, 'sessions'),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
        startAfter(lastVisible),
        limit(SESSIONS_PER_PAGE)
      );
      
      const querySnapshot = await getDocs(q);
      const newSessions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Session[];

      set((state) => ({ 
        sessions: [...state.sessions, ...newSessions],
        isLoading: false,
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || state.lastVisible,
        hasMore: querySnapshot.docs.length === SESSIONS_PER_PAGE
      }));
    } catch (error) {
      console.error('Error loading more sessions:', error);
      set({ isLoading: false });
    }
  }
})); 